import { AlertTriangle, Info, Lock, Target, XOctagon } from "lucide-react";
import type { ReactNode } from "react";

import type { LockReason, ValidationFinding } from "@/api/hooks";
import { cn } from "@/lib/cn";
import { fieldIdentity } from "@/lib/fieldState";
import {
  FAILURE_CLASS_LABEL,
  LOCK_REASON_DETAIL,
  LOCK_REASON_LABEL,
  SEVERITY_LABEL,
} from "@/lib/labels";
import { classifyFinding, isGuardFloorClass } from "@/lib/validationCodes";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";

import { FindingLocation } from "./FindingLocation";

/**
 * One validation finding.
 *
 * What may appear here is bounded by the contract: `severity`, `code`,
 * `message`, and `path`, plus the lock reason joined in from the effective
 * projection. `message` is contractually operator-facing and never clinical
 * free text, and nothing is synthesised from a `FieldState.value` — so there is
 * no route by which clinical content reaches this component.
 *
 * Severity is carried by an icon **and** a text label, never by colour alone.
 */

const SEVERITY_ICON: Record<ValidationFinding["severity"], ReactNode> = {
  error: <XOctagon aria-hidden="true" className="h-4 w-4" />,
  warning: <AlertTriangle aria-hidden="true" className="h-4 w-4" />,
  info: <Info aria-hidden="true" className="h-4 w-4" />,
};

const SEVERITY_TONE: Record<ValidationFinding["severity"], string> = {
  error: "text-destructive",
  warning: "text-warning-foreground",
  info: "text-muted-foreground",
};

export function FindingItem({
  finding,
  /**
   * `lock.reason` for the field this finding names, when the effective
   * projection for the section has loaded and the path resolved. This is the
   * join that makes a locked-floor diagnostic possible: neither the report nor
   * the projection identifies a guard-floor violation alone.
   */
  lockReason,
  /** Supplied only when `finding.path` resolved to a rendered control. */
  onJump,
  /** The Project the report was run for (#1084's Pathways settings link). */
  projectId,
  /** The adopted item's catalog name, when `finding.location` names one. */
  assetName,
}: {
  finding: ValidationFinding;
  lockReason?: LockReason | undefined;
  onJump?: (() => void) | undefined;
  projectId?: string | undefined;
  assetName?: string | null | undefined;
}) {
  const failureClass = classifyFinding(finding);

  // The lock badge is shown for any lock reason, not only the guard floor: that
  // a finding names a field locked by lifecycle or by an external source is
  // information the clinician needs to know why they cannot act on it.
  //
  // It is suppressed in exactly one case — a `floor.` code on a `safety_floor`
  // field — where the class chip and the lock badge would carry the same words
  // twice. The join is then expressed by the class chip plus the lock's own
  // explanation below.
  const bothSayGuardFloor =
    isGuardFloorClass(failureClass) && lockReason === "safety_floor";
  const showLockBadge = lockReason !== undefined && !bothSayGuardFloor;

  return (
    <li className="flex flex-col gap-2 border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <span
          className={cn(
            "mt-0.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide",
            SEVERITY_TONE[finding.severity],
          )}
        >
          {SEVERITY_ICON[finding.severity]}
          {SEVERITY_LABEL[finding.severity] ?? finding.severity}
        </span>

        <p className="min-w-0 flex-1 text-sm text-foreground">
          {finding.message}
        </p>

        {onJump ? (
          <Button variant="outline" size="sm" onClick={onJump}>
            <Target aria-hidden="true" className="h-4 w-4" />
            {/*
              Names its destination. "Go to field" on six findings is six
              controls with the same accessible name and no way to tell them
              apart in a screen-reader control listing.
            */}
            <span className="sr-only">
              Go to field {fieldIdentity(finding.path ?? "")}
            </span>
            <span aria-hidden="true">Go to field</span>
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={failureClass === "unclassified" ? "outline" : "neutral"}>
          {FAILURE_CLASS_LABEL[failureClass]}
        </Badge>

        {/*
          The raw code, always. For a classified finding it is the machine-
          readable identity of the rule; for an unclassified one it is the only
          thing this editor can honestly say about it. Never hidden, because
          hiding it is how an unrecognised code becomes an invisible finding.
        */}
        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
          {finding.code}
        </code>

        {showLockBadge && lockReason ? (
          <Badge tone="warning">
            <Lock aria-hidden="true" className="mr-1 h-3 w-3" />
            {LOCK_REASON_LABEL[lockReason] ?? "Locked"}
          </Badge>
        ) : null}

        {finding.location ? (
          // #1084: a typed clinical-dependency finding carries the SERVICE'S
          // attribution — the adopted item, the base pack, the Project's own
          // pathway settings, or an honest "cannot be linked". It replaces the
          // field identity below, whose `path` is an asset id or a pack file
          // here and would humanise into a name that is neither.
          <FindingLocation
            location={finding.location}
            projectId={projectId}
            assetName={assetName}
          />
        ) : finding.path ? (
          // #488: the field's HUMAN IDENTITY, not its dotted address.
          //
          // The amendment removes dotted paths from every Project route, and
          // this was one of three places they reached one. What a clinician
          // needs from a finding is which field it concerns, and `fieldIdentity`
          // derives that from the same path without rendering it.
          //
          // WHY THE WHOLE HIERARCHY AND NOT THE LEAF. Round 1 of this pull
          // request rendered the last segment alone, which made
          // `summary.en.fixed_values.title` and its Hebrew twin both read
          // "Title" — and because this change also removed the rendered field
          // controls, two findings sharing a rule and a message became
          // impossible to tell apart on this screen. That is the validation and
          // evidence behavior the ticket requires be preserved, so the identity
          // renders every segment.
          //
          // The finding still carries `path` on the contract type; `FindingList`
          // reads it to resolve anchors and the lock-reason join matches on it.
          // What changed is that it is no longer rendered.
          <span className="text-xs text-muted-foreground">
            {fieldIdentity(finding.path)}
            {!onJump ? (
              <span className="ml-2 italic">
                {/*
                  Stated rather than left as a missing button. A finding whose
                  control is not on this screen is still a finding; silently
                  dropping the affordance would read as "this one has no
                  location", which is a different and false claim. Since #488
                  this is the ordinary case on a Project route — the review
                  surface renders no field controls at all.
                */}
                not shown on this screen
              </span>
            ) : null}
          </span>
        ) : (
          <span className="text-xs italic text-muted-foreground">
            No field reported
          </span>
        )}
      </div>

      {lockReason ? (
        <p className="text-xs text-muted-foreground">
          {LOCK_REASON_DETAIL[lockReason]}
        </p>
      ) : null}
    </li>
  );
}
